import { useQuery } from '@tanstack/react-query';
import type { TeamTaskMemberSummary } from '@demind-inc/core';

import { teamApi } from '../api';
import { getDueDateYYYYMMDD } from '../../helpers';
import {
  AssignedTeamTask,
  AssignedTeamTasksByDate,
  AssignedTeamTasksRangeResult,
  TEAM_ASSIGNED_TASKS_QUERY_KEY,
  buildAssignedTeamTask,
} from './teamTasks.shared';

interface UseAssignedTeamTasksRangeParams {
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export const useAssignedTeamTasksRange = ({
  userId,
  startDate,
  endDate,
}: UseAssignedTeamTasksRangeParams) => {
  const hasAssignedTeamTask = (task: AssignedTeamTask | null): task is AssignedTeamTask => !!task;

  const assignedTeamTasksQuery = useQuery({
    queryKey: [...TEAM_ASSIGNED_TASKS_QUERY_KEY, { userId, startDate, endDate }],
    queryFn: () => {
      return teamApi.getAssignedTeamTasksRange(startDate!, endDate!).then(({ data }) => {
        const firstResult = data.results?.[0];
        const teamMembersByTeamId = (data.teamMembersByTeamId ?? {}) as Record<
          string,
          TeamTaskMemberSummary[]
        >;
        const tasksByDate: AssignedTeamTasksByDate[] = (data.results ?? []).map((result) => ({
          date: result.date,
          tasks: (result.tasks ?? [])
            .map(buildAssignedTeamTask)
            .filter(hasAssignedTeamTask)
            .filter((task) => task.assignedTeamTask.bucket !== 'overdue')
            .filter((task) => getDueDateYYYYMMDD(task.dueDateTime) === result.date),
          updatedAt: new Date().toISOString(),
        }));
        const overdueTasks =
          firstResult?.tasks
            ?.map(buildAssignedTeamTask)
            .filter(hasAssignedTeamTask)
            .filter((task) => task.assignedTeamTask.bucket === 'overdue') ?? [];

        const result: AssignedTeamTasksRangeResult = {
          startDate: data.startDate,
          endDate: data.endDate,
          tasksByDate,
          overdueTasks,
          teamMembersByTeamId,
        };

        return result;
      });
    },
    refetchOnWindowFocus: false,
    enabled: !!userId && !!startDate && !!endDate,
    staleTime: 15000,
  });

  return {
    assignedTeamTasksByDate: assignedTeamTasksQuery.data?.tasksByDate ?? [],
    assignedOverdueTeamTasks: assignedTeamTasksQuery.data?.overdueTasks ?? [],
    assignedTeamTaskMembersByTeamId: assignedTeamTasksQuery.data?.teamMembersByTeamId ?? {},
    ...assignedTeamTasksQuery,
  };
};
